import { Component, Input, OnInit } from '@angular/core'
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { Store } from '@ngrx/store'
import { Observable } from 'rxjs'
import { take } from 'rxjs/operators'

import { EquipmentTemplateCoreDetails } from '@app/modules/equipment/models/equipment-template-core-details.model'
import { Process } from '@app/modules/equipment/models/process.model'
import { TemplateTypeOption } from '@app/modules/equipment/models/template-type.model'
import { WeightTemplateValidationsService } from '@app/modules/equipment/services/template-validations/weight-template-validations.service'
import { UOM } from '@app/modules/shared/models/engineering-units/unit-of-measurement.model'
import { SafeUnsubscriberComponent } from '@app/safe-unsubscriber.component'
import { AppState } from '@app/store/app.store'
import * as EquipmentTemplateAction from '@app/store/equipment/actions/equipment-template.actions'
import * as TemplateFormAction from '@app/store/equipment/actions/template-form.actions'
import { equipmentTemplateList, selectedTemplate } from '@app/store/equipment/selectors/equipment-template.selectors'
import { process } from '@app/store/equipment/selectors/process.selectors'
import { uomList } from '@app/store/equipment/selectors/uom.selectors'
import { mapUomObjToUomDisplay } from '@app/utils/uom/uom.util'
import { AdminFormRowComponent } from '../admin-form-row/admin-form-row.component'
import { DropdownComponent } from '@app/modules/shared/components/dropdown/dropdown.component'
import { AsyncPipe, NgClass } from '@angular/common'
import { FilterPercentUom } from '@app/modules/shared/pipes/filter-percent-uom.pipe'
import { NumericInputComponent } from '@app/modules/mrma-ui-components/controls/numeric-input/numeric-input.component'

@Component({
    selector: 'app-weight',
    templateUrl: './weight.component.html',
    standalone: true,
    imports: [
        NgClass,
        ReactiveFormsModule,
        AdminFormRowComponent,
        DropdownComponent,
        AsyncPipe,
        FilterPercentUom,
        NumericInputComponent
    ]
})
export class WeightComponent extends SafeUnsubscriberComponent implements OnInit {
    @Input() isAdmin: boolean

    public uomList$: Observable<UOM[]>
    public processes$: Observable<Process[]>
    public weightTemplateForm: UntypedFormGroup
    public mapUomObjToUomDisplay = mapUomObjToUomDisplay
    private selectedTemplate: TemplateTypeOption
    private templateDetails: EquipmentTemplateCoreDetails

    constructor(
        private store: Store<AppState>,
        private formBuilder: UntypedFormBuilder,
        private weightTemplateValidation: WeightTemplateValidationsService
    ) {
        super()
    }

    ngOnInit(): void {
        this.uomList$ = this.store.select(uomList)
        this.processes$ = this.store.select(process)
        this.addSubscription(
            this.store.select(selectedTemplate).subscribe(template => this.selectedTemplate = template)
        )
        this.initTemplateData()
        this.watchMainFormControl()
    }

    private watchMainFormControl(): void {
        this.addSubscription(
            this.weightTemplateForm.valueChanges.subscribe(form => {
                this.store.dispatch(new TemplateFormAction.SelectCurrentTemplateFormsAction(form))
                this.store.dispatch(new EquipmentTemplateAction.EquipmentTemplateWasModifiedAction(
                    this.templateDetails,
                    this.weightTemplateValidation.isValid(this.weightTemplateForm.value)
                ))
            })
        )
    }

    private initTemplateData(): void {
        this.store.select(equipmentTemplateList).pipe(take(1)).subscribe(equipmentTemplate => {
            this.templateDetails = equipmentTemplate.find(template =>
                template.id === this.selectedTemplate.templateUniqueId
            )

            this.weightTemplateForm = this.formBuilder.group({
                process: {
                    id: (this.templateDetails?.processId ?? 1),
                    name: (this.templateDetails?.processName ?? 'Generic')
                },
                alias: (this.templateDetails?.detail?.alias ?? ''),
                input: this.formBuilder.group({
                    unitOfMeasurement: (this.templateDetails?.detail?.input?.unitOfMeasurement ?? null),
                    maximumRange: (this.templateDetails?.detail?.input?.maximumRange ?? 0),
                    minimumRange: (this.templateDetails?.detail?.input?.minimumRange ?? 0),
                }),
                tolerance: this.formBuilder.group({
                    unitOfMeasurement: (this.templateDetails?.detail?.tolerance?.unitOfMeasurement ?? null),
                    value: (this.templateDetails?.detail?.tolerance?.value ?? 0),
                }),
                procedure: (this.templateDetails?.detail?.procedure ?? '')
            })
            this.store.dispatch(new TemplateFormAction.SelectCurrentTemplateFormsAction(
                this.weightTemplateForm.value
            ))

            if (!this.isAdmin) {
                this.weightTemplateForm.disable()
            }
        })
    }
}
